import { AGING_BUCKETS, formatCurrency } from '@jobos/shared';
import type { AgingBucket, WorkspaceInvoiceRow } from '@jobos/shared';

/**
 * Stage 3's 30 / 60 / 90 boundaries (FR-2.2).
 *
 * ONE list sorted oldest-first with group headers, NOT four collapsible
 * buckets — it reads as a worklist, which is what stage 3 is. A header appears
 * on the first row of each bucket and nowhere else.
 *
 * The bucket is the server's; this file only decides where the labels go.
 */

export const AGING_GROUP_LABELS: Record<AgingBucket, string> = {
  '0-30': 'Up to 30 days',
  '31-60': '31 to 60 days',
  '61-90': '61 to 90 days',
  '90+': 'Over 90 days',
};

export interface AgingGroup {
  bucket: AgingBucket;
  label: string;
  /** Total still owed in this bucket — the reason to look at it first. */
  outstanding: number;
  count: number;
}

/**
 * Which row opens each bucket, keyed by invoice id, plus that bucket's totals.
 *
 * Rows arrive already sorted oldest-first, so a bucket opens at the first row
 * carrying it. Computing this from the whole page rather than per row keeps
 * the totals honest: a header claiming "$4,200 outstanding" must count every
 * row under it, not just the one it sits on.
 */
export function agingGroups(rows: WorkspaceInvoiceRow[]): Map<string, AgingGroup> {
  const totals = new Map<AgingBucket, { outstanding: number; count: number }>();
  const openedBy = new Map<AgingBucket, string>();

  for (const row of rows) {
    const bucket = row.agingBucket;
    // A row with no bucket was never sent, so it is not ageing. It cannot open
    // a group and must not be counted in one.
    if (!bucket) continue;
    if (!openedBy.has(bucket)) openedBy.set(bucket, row.invoiceId);
    const t = totals.get(bucket) ?? { outstanding: 0, count: 0 };
    t.outstanding += row.outstanding;
    t.count += 1;
    totals.set(bucket, t);
  }

  const byInvoiceId = new Map<string, AgingGroup>();
  // Declaration order, so headers read oldest-last down the page in the same
  // order the buckets are defined rather than the order they happen to appear.
  for (const bucket of AGING_BUCKETS) {
    const invoiceId = openedBy.get(bucket);
    const t = totals.get(bucket);
    if (!invoiceId || !t) continue;
    byInvoiceId.set(invoiceId, {
      bucket,
      label: AGING_GROUP_LABELS[bucket],
      outstanding: t.outstanding,
      count: t.count,
    });
  }
  return byInvoiceId;
}

export const agingGroupSummary = (g: AgingGroup) =>
  `${g.count} ${g.count === 1 ? 'invoice' : 'invoices'} · ${formatCurrency(g.outstanding)} outstanding`;
